// @ts-expect-error: Missing type definitions for react-force-graph-2d
import { NodeObject } from 'react-force-graph-2d'
import { NodeDrawingSettings, getScaledNodeSettings } from '@unholster/graphkit'
import { truncate, splitTextIntoLines, isPep } from '@/lib/utils'

interface DrawNodeBase {
  x: number
  y: number
  radius: number
  height: number
  ctx: CanvasRenderingContext2D
}

interface DrawCircleNodeProps extends DrawNodeBase {
  label: string
  node: NodeObject
  maxLabelLength: number
  scale: number
  mainTextColor?: string
  secondTextColor?: string
  fontSize: number
}

interface DrawCircleNodeRingProps extends DrawNodeBase {
  offset: number
  ringWidth: number
  ringColor: string
}

interface DrawPepProps {
  drawingSettings: NodeDrawingSettings
  node: NodeObject
  ctx: CanvasRenderingContext2D
  globalScale: number
}

export const drawCircleNodeRing = ({
  x,
  y,
  ctx,
  radius,
  offset,
  ringWidth,
  ringColor,
}: DrawCircleNodeRingProps) => {
  ctx.beginPath()
  ctx.arc(x + radius, y + radius, radius + offset, 0, Math.PI * 2)
  ctx.closePath()
  ctx.strokeStyle = ringColor
  ctx.lineWidth = ringWidth
  ctx.stroke()
}

export const drawCircleNode = ({
  x,
  y,
  radius,
  ctx,
  label,
  node,
  maxLabelLength,
  scale,
  mainTextColor = 'white',
  secondTextColor = 'black',
  fontSize,
}: DrawCircleNodeProps) => {
  // Draw circle
  ctx.beginPath()
  ctx.arc(x + radius, y + radius, radius, 0, Math.PI * 2)
  ctx.closePath()
  ctx.fillStyle = node.isMain ? node.color : node.secondColor
  ctx.fill()

  // Text style
  const computedFontSize = fontSize * scale
  ctx.font = `${computedFontSize}px "Hanken Grotesk"`
  ctx.fillStyle = node.isMain ? mainTextColor : secondTextColor

  const truncatedLabel = truncate(label, maxLabelLength)
  const lineHeight = computedFontSize * 1.2
  const maxTextWidth = 2 * radius * 0.85 * scale
  const lines = splitTextIntoLines(truncatedLabel, maxTextWidth, ctx)
  const centerX = x + radius
  const centerY = y + radius
  const totalTextHeight = lines.length * lineHeight
  const startY = centerY - totalTextHeight / 2 + lineHeight / 2

  ctx.save()
  ctx.translate(centerX, centerY)
  ctx.scale(1 / scale, 1 / scale)

  lines.forEach((line, index) => {
    ctx.fillText(line, 0, startY - centerY + index * lineHeight)
  })
  ctx.restore()
}

export const drawPepNode = ({ drawingSettings, node, ctx, globalScale }: DrawPepProps) => {
  const {
    nodeHeight: height,
    nodeWidth: width,
    nodeRadius: radius,
    x,
    y,
    ringOffset,
    ringWidth,
  } = getScaledNodeSettings(drawingSettings, globalScale, node)

  // Draw ring
  ctx.beginPath()
  ctx.arc(x + radius + width, y + radius - height, radius + ringOffset, 0, Math.PI * 2)
  ctx.closePath()
  ctx.strokeStyle = 'white'
  ctx.lineWidth = ringWidth
  ctx.stroke()

  // Draw circle
  ctx.beginPath()
  ctx.arc(x + radius + width, y + radius - height, radius, 0, Math.PI * 2)
  ctx.closePath()
  ctx.fillStyle = 'red'
  ctx.fill()
}

export interface ParticleState {
  linkId: string
  t: number
  speed: number
  width: number
}

export const createNodeDrawingSettings = (
  overrides: Partial<NodeDrawingSettings> = {}
): NodeDrawingSettings => ({
  fontSize: 11,
  maxLabelLength: 26,
  textAlign: 'center',
  textBaseline: 'middle',
  mainTextColor: 'white',
  secondTextColor: 'black',
  scaleFactor: 1,
  nodeWidth: 100,
  nodeHeight: 100,
  nodeRadius: 50,
  nodeRingOffset: 0,
  nodeRingWidth: 5,
  nodeRingColor: 'blue',
  ...overrides,
})

export const nodeDrawingSettings = createNodeDrawingSettings()
export const pepDrawingSettings = createNodeDrawingSettings({
  maxLabelLength: 16,
  nodeWidth: 65,
  nodeHeight: 25,
  nodeRadius: 4,
  nodeRingWidth: 3,
})

export const customNode = (
  node: NodeObject,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
  isSelected = false,
  isHovered = false,
  isHighlighted = false
) => {
  const settings = getScaledNodeSettings(nodeDrawingSettings, globalScale, node)
  const ringColor = isSelected
    ? node.color
    : isHovered
    ? 'red'
    : isHighlighted
    ? 'lightblue'
    : nodeDrawingSettings.nodeRingColor

  ctx.textAlign = nodeDrawingSettings.textAlign
  ctx.textBaseline = nodeDrawingSettings.textBaseline

  drawCircleNode({
    ...settings,
    radius: settings.nodeRadius,
    height: settings.nodeHeight,
    ctx,
    label: node.label as string,
    node,
    maxLabelLength: nodeDrawingSettings.maxLabelLength,
    scale: globalScale,
    mainTextColor: nodeDrawingSettings.mainTextColor,
    secondTextColor: node.fontColor,
  })

  if (isSelected || isHovered || isHighlighted) {
    drawCircleNodeRing({
      x: settings.x,
      y: settings.y,
      radius: settings.nodeRadius,
      height: settings.nodeHeight,
      offset: settings.ringOffset,
      ringWidth: settings.ringWidth,
      ringColor,
      ctx,
    })
  }

  if (node.data?.politicamente_expuesto) {
    const isPEP = isPep(node.data.politicamente_expuesto)
    if (isPEP) drawPepNode({ drawingSettings: pepDrawingSettings, globalScale, ctx, node })
  }
}

function getArcCenter(sx: number, sy: number, tx: number, ty: number, curvature: number) {
  const dx = tx - sx
  const dy = ty - sy
  const distance = Math.hypot(dx, dy)
  const midX = (sx + tx) / 2
  const midY = (sy + ty) / 2
  const perpX = -dy / curvature
  const perpY = dx / curvature
  return {
    cx: midX + perpX * distance,
    cy: midY + perpY * distance,
  }
}

function getAngleDelta(startAngle: number, endAngle: number, anticlockwise: boolean): number {
  let delta = endAngle - startAngle
  if (anticlockwise && delta > 0) {
    delta -= 2 * Math.PI
  } else if (!anticlockwise && delta < 0) {
    delta += 2 * Math.PI
  }
  return delta
}

function getArcPoint(
  t: number,
  sx: number,
  sy: number,
  tx: number,
  ty: number,
  cx: number,
  cy: number,
  anticlockwise = false
) {
  const radius = Math.hypot(sx - cx, sy - cy)
  const startAngle = Math.atan2(sy - cy, sx - cx)
  const endAngle = Math.atan2(ty - cy, tx - cx)
  const delta = getAngleDelta(startAngle, endAngle, anticlockwise)
  const angle = startAngle + t * delta
  return {
    x: cx + radius * Math.cos(angle),
    y: cy + radius * Math.sin(angle),
  }
}

export function customEdge(
  link: any,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
  labelInfoRef?: React.MutableRefObject<any[]>
) {
  const scaleToShowLabel = 4
  if (globalScale < scaleToShowLabel) labelInfoRef = undefined
  const { source, target, curvature = 55, bidirectional, direction } = link
  if (!source || !target) return

  const sx = source.x,
    sy = source.y
  const tx = target.x,
    ty = target.y
  if (
    typeof sx !== 'number' ||
    typeof sy !== 'number' ||
    typeof tx !== 'number' ||
    typeof ty !== 'number'
  ) {
    return
  }

  const directionFactor = bidirectional && typeof direction === 'number' ? direction : 1
  const anticlockwise = directionFactor < 0

  const { cx, cy } = getArcCenter(sx, sy, tx, ty, curvature)
  const radius = Math.hypot(sx - cx, sy - cy)
  const startAngle = Math.atan2(sy - cy, sx - cx)
  const endAngle = Math.atan2(ty - cy, tx - cx)
  const delta = getAngleDelta(startAngle, endAngle, anticlockwise)
  const fullAngle = Math.abs(delta)

  ctx.beginPath()
  ctx.moveTo(sx, sy)
  ctx.arc(cx, cy, radius, startAngle, startAngle + delta, anticlockwise)
  ctx.strokeStyle = '#828282'
  ctx.lineWidth = 2 / globalScale
  ctx.stroke()

  if (globalScale >= scaleToShowLabel) {
    const { nodeRadius: targetRadius } = getScaledNodeSettings(
      nodeDrawingSettings,
      globalScale,
      target
    )

    const totalDistance = radius * fullAngle
    const arcDist = totalDistance - targetRadius
    const arrowAngle = startAngle + Math.sign(delta) * (arcDist / radius)
    const arrowPosX = cx + radius * Math.cos(arrowAngle)
    const arrowPosY = cy + radius * Math.sin(arrowAngle)
    const arrowRotation = arrowAngle + (Math.PI / 2) * Math.sign(delta)

    const arrowLength = 14 / globalScale
    const arrowWidth = 7 / globalScale

    ctx.save()
    ctx.translate(arrowPosX, arrowPosY)
    ctx.rotate(arrowRotation)
    ctx.beginPath()
    ctx.moveTo(0, 0)
    ctx.lineTo(-arrowLength, arrowWidth)
    ctx.lineTo(-arrowLength, -arrowWidth)
    ctx.closePath()
    ctx.fillStyle = '#828282'
    ctx.fill()
    ctx.restore()

    const tLabel = 0.55
    const labelAngle = startAngle + Math.sign(delta) * (tLabel * fullAngle)
    const labelX = cx + radius * Math.cos(labelAngle)
    const labelY = cy + radius * Math.sin(labelAngle)
    if (link.label && labelInfoRef) {
      labelInfoRef.current.push({ label: link.label, labelX, labelY, globalScale })
    }
  }
}

export function drawParticles(
  link: any,
  ctx: CanvasRenderingContext2D,
  globalScale: number,
  particles: ParticleState[]
) {
  const scaleToShowLabel = 4
  if (globalScale < scaleToShowLabel) return

  const linkParticles = particles.filter((p) => p.linkId === link.id)
  if (!linkParticles.length) return

  const { source, target, curvature = 55, bidirectional, direction } = link
  if (!source || !target) return

  const sx = source.x,
    sy = source.y
  const tx = target.x,
    ty = target.y
  if (
    typeof sx !== 'number' ||
    typeof sy !== 'number' ||
    typeof tx !== 'number' ||
    typeof ty !== 'number'
  ) {
    return
  }

  const directionFactor = bidirectional && typeof direction === 'number' ? direction : 1
  const anticlockwise = directionFactor < 0
  const { cx, cy } = getArcCenter(sx, sy, tx, ty, curvature)

  linkParticles.forEach((particle) => {
    const { x, y } = getArcPoint(particle.t, sx, sy, tx, ty, cx, cy, anticlockwise)
    const particleRadius = particle.width / globalScale

    ctx.save()
    ctx.beginPath()
    ctx.arc(x, y, particleRadius, 0, 2 * Math.PI)
    ctx.fillStyle = '#828282'
    ctx.fill()
    ctx.restore()
  })
}

export const drawEdgeLabel = (
  ctx: CanvasRenderingContext2D,
  {
    label,
    labelX,
    labelY,
    globalScale,
  }: { label: string; labelX: number; labelY: number; globalScale: number }
) => {
  const fontSize = 11 / globalScale
  ctx.font = `${fontSize}px "Hanken Grotesk"`
  const textWidth = ctx.measureText(label).width
  const boxWidth = textWidth + 16 / globalScale
  const boxHeight = fontSize + 8 / globalScale

  ctx.fillStyle = '#ffffff'
  ctx.fillRect(labelX - boxWidth / 2, labelY - boxHeight / 2, boxWidth, boxHeight)
  ctx.strokeStyle = '#828282'
  ctx.lineWidth = 1.5 / globalScale
  ctx.strokeRect(labelX - boxWidth / 2, labelY - boxHeight / 2, boxWidth, boxHeight)

  ctx.fillStyle = 'black'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.fillText(label, labelX, labelY)
}
